<template>
  <v-flex class="pa-0">
    <v-card
      v-if="product.children && product.children.length"
    >
      <v-card-title>
        <h4>{{ $t('Product Variants') }}</h4>
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <v-icon
              color="primary"
              class="pointer"
              dark
              v-on="on">
              info
            </v-icon>
          </template>
          <span>{{ $t('Will propagate selected Attributes from Parent product to selected Variations.') }}</span>
        </v-tooltip>

        <v-spacer/>
        <v-btn
          v-if="product.id && product.children && product.children.length"
          :disabled="!selected.length"
          flat
          class="text--secondary text-capitalize"
          @click="attributesDialog = true"
        >
          {{ $t('Update Attributes') }}
        </v-btn>
      </v-card-title>

      <v-divider/>

      <v-card-text>
        <v-data-table
          v-model="selected"
          :items="product.children"
          :headers="headers"
          hide-actions
          item-key="id"
          disable-initial-sort
          select-all
          show-select
        >
          <template v-slot:items="props">
            <tr>
              <td>
                <v-checkbox
                  v-model="props.selected"
                  hide-details
                />
              </td>
              <td>
                <v-img
                  :src="$helpers.imageFrom(props.item, 'tiny')"
                  min-height="45"
                  min-width="45"
                  class="my-1"
                />
              </td>
              <td>
                <router-link
                  :to="{
                    name: 'single-product',
                    params: { id: props.item.id }
                  }"
                  class="text-no-wrap"
                  target="_blank"
                >
                  {{ props.item.sku }}
                </router-link>
              </td>
              <td>{{ props.item.name }}</td>
              <td>{{ props.item.inventory.quantity }}</td>
              <td class="text-no-wrap">{{ props.item.real_price | money }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="attributesDialog"
      max-width="600px"
      scrollable
    >
      <v-card max-height="700px">
        <v-card-title>
          <h3>{{ $t('Select attributes to update') }}</h3>

          <v-spacer/>
          <v-btn
            icon
            flat
            @click="close"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-treeview
            v-model="attributesToUpdate"
            :items="allowedAttributes"
            item-key="value"
            item-text="text"
            selectable
            activatable
            open-on-click
            transition
            return-object
          />
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            :loading="updating"
            class="text-capitalize"
            color="primary"
            @click="updateAttributes"
          >
            {{ $t('Update') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
export default {
  name: 'ProductSingleVariants',

  props: {
    product: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      selected: [],
      attributesDialog: false,
      attributesToUpdate: [],
      open: [],
      updating: false
    }
  },

  computed: {
    headers () {
      return [
        { text: '', value: null, sortable: false },
        { text: this.$t('SKU'), value: 'sku' },
        { text: this.$t('Name'), value: 'name' },
        { text: this.$t('Inventory'), value: 'name' },
        { text: this.$t('Price'), value: 'price' }
      ]
    },

    allowedAttributes () {
      return [
        {
          text: this.$t('Inventory'),
          value: 'inventory',
          children: [
            { text: this.$t('Policy'), value: 'policy', parent: 'inventory' },
            { text: this.$t('Trackable'), value: 'track', parent: 'inventory' },
            { text: this.$t('Restockable'), value: 'is_restockable', parent: 'inventory' },
            { text: this.$t('Available'), value: 'is_available', parent: 'inventory' },
            { text: this.$t('Quantity'), value: 'quantity', parent: 'inventory' },
            { text: this.$t('Min per order'), value: 'min_per_order', parent: 'inventory' },
            { text: this.$t('Max per order'), value: 'max_per_order', parent: 'inventory' },
            { text: this.$t('Bundle exclusive'), value: 'is_bundle_exclusive', parent: 'inventory' }
          ]
        },
        { text: this.$t('Translations'), value: 'translations' },
        { text: this.$t('Properties'), value: 'properties' },
        { text: this.$t('Type'), value: 'type' },
        { text: this.$t('Brand'), value: 'brand' },
        { text: this.$t('Price'), value: 'price' },
        { text: this.$t('Sale Price'), value: 'sale_price' },
        { text: this.$t('Gross Price'), value: 'gross_price' },
        { text: this.$t('Gross Sale Price'), value: 'gross_sale_price' },
        { text: this.$t('Tags'), value: 'tags' },
        { text: this.$t('Taxonomy'), value: 'taxonomy_id' },
        { text: this.$t('Image'), value: 'image_url' },
        { text: this.$t('Application Restrictions'), value: 'application' },
        { text: this.$t('Related Products/Taxonomy'), value: 'related' },
        { text: this.$t('Sort Order'), value: 'order' },
        { text: this.$t('Customer Groups Restrictions'), value: 'customer_group' },
        { text: this.$t('Tier Prices'), value: 'tiers' }
      ]
    }
  },

  methods: {
    updateAttributes () {
      this.updating = true
      const attributes = []
      const inventory = []
      let withTranslations = false

      this.attributesToUpdate.forEach(attr => {
        if (attr.value === 'translations') {
          withTranslations = true
        }
        if (attr.children && attr.children.length) {
          return
        }
        if (attr.parent && attr.parent === 'inventory') {
          inventory.push(attr.value)
          return
        }
        attributes.push(attr.value)
      })

      if (inventory.length) {
        attributes.push({ inventory: inventory })
      }

      const request = {
        attributes: attributes,
        ids: this.selected.map(product => product.id)
      }

      if (withTranslations) {
        request.translations = this.product.translations
      }

      this.product.updateVariantsAttributes(request)
        .then(() => {
          this.close()
          this.selected = []
          this.$toast.success(this.$t('Variations attributes updated'))
        }).catch(e => {
          this.$toast.error(e)
        }).finally(() => {
          this.updating = false
        })
    },

    close () {
      this.attributesDialog = false
      this.attributesToUpdate = []
    }
  }
}
</script>
